import React, { useState, useEffect, useRef } from 'react';
import { Plus, Pencil, Trash2, Upload, Loader2, Vote, ExternalLink, Share2, ShieldCheck, RefreshCw, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { api, fileUrl } from '../../lib/api';

const EMPTY = { part: 1, title: '', image_url: '', explanation: '', sources_text: '', article_url: '', article_label: '', share_title: '', share_description: '', share_image_url: '', published: true };

// Witness Your Vote asset manager (own tab): upload / replace / preview /
// remove the official logo (+ ALT text), plus welcome video and site link.
// Empty fields keep the approved placeholders. Saving updates every public
// place the WYV logo appears (including /witness-your-vote) immediately.
const WyvAssetsCard = () => {
  const [wyv, setWyv] = useState({ logo_url: '', logo_alt: '', video_url: '', video_embed_url: '', site_url: '' });
  const [active, setActive] = useState({ logo_url: '', logo_alt: '' }); // last SAVED state
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const logoRef = useRef(null);

  useEffect(() => {
    api.get('/admin/wyv-assets').then((r) => {
      setWyv((w) => ({ ...w, ...r.data }));
      setActive({ logo_url: r.data.logo_url || '', logo_alt: r.data.logo_alt || '' });
    }).catch(() => {});
  }, []);

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', 'witness-your-vote');
      const r = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setWyv((w) => ({ ...w, logo_url: r.data.url }));
      toast.success('Logo uploaded. Click Save to publish it.');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); } finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put('/admin/wyv-assets', wyv);
      setActive({ logo_url: r.data.logo_url || '', logo_alt: r.data.logo_alt || '' });
      toast.success('Saved. The public Witness Your Vote page is updated.');
    } catch { toast.error('Save failed'); } finally { setSaving(false); }
  };

  const unsaved = wyv.logo_url !== active.logo_url || (wyv.logo_alt || '') !== (active.logo_alt || '');

  return (
    <Card data-testid="admin-wyv-assets-card">
      <CardContent className="p-5 sm:p-6 space-y-6">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-lg font-black text-[#1a2744]">Witness Your Vote Assets</h2>
            {active.logo_url ? (
              <Badge className="bg-emerald-100 text-emerald-900 border border-emerald-300 font-bold" data-testid="admin-wyv-logo-status-active">Official logo ACTIVE</Badge>
            ) : (
              <Badge variant="outline" className="bg-amber-50 text-amber-900 border-amber-300 font-bold" data-testid="admin-wyv-logo-status-placeholder">Placeholder mark showing</Badge>
            )}
            {unsaved && <Badge variant="outline" className="bg-slate-100 text-slate-600 font-bold" data-testid="admin-wyv-unsaved-badge">Unsaved changes</Badge>}
          </div>
          <p className="text-sm text-slate-500 mt-1">Upload the official logo and it appears everywhere the Witness Your Vote logo shows on the public website. Empty fields keep the approved placeholders. No code changes needed.</p>
        </div>

        {/* ---- Official logo: upload / replace / preview / remove / ALT ---- */}
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-4" data-testid="admin-wyv-logo-block">
          <p className="text-xs font-black uppercase tracking-wider text-slate-500">Official Logo</p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="shrink-0">
              {wyv.logo_url ? (
                <img src={fileUrl(wyv.logo_url)} alt={wyv.logo_alt || 'Witness Your Vote logo preview'} className="h-24 w-auto max-w-[200px] object-contain rounded-xl border border-slate-200 bg-white p-2" data-testid="admin-wyv-logo-preview" />
              ) : (
                <div className="h-24 w-24 rounded-xl bg-white border border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400">
                  <Vote className="h-6 w-6" />
                  <p className="text-[10px] font-bold mt-1">No logo yet</p>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={(e) => { uploadLogo(e.target.files?.[0]); e.target.value = ''; }} data-testid="admin-wyv-logo-input" />
              <Button className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-black tracking-wide btn-press" onClick={() => logoRef.current?.click()} disabled={uploading} data-testid="admin-wyv-logo-upload">
                {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />} {wyv.logo_url ? 'REPLACE LOGO' : 'UPLOAD WITNESS YOUR VOTE LOGO'}
              </Button>
              {wyv.logo_url && (
                <Button variant="outline" className="h-10 text-xs text-red-600 border-red-200 hover:bg-red-50 font-bold btn-press block" onClick={() => setWyv((w) => ({ ...w, logo_url: '' }))} data-testid="admin-wyv-logo-remove">Remove logo (placeholder returns)</Button>
              )}
            </div>
          </div>
          <div>
            <Label htmlFor="wyv-logo-alt">Logo ALT text (describes the logo for accessibility)</Label>
            <Input id="wyv-logo-alt" value={wyv.logo_alt || ''} onChange={(e) => setWyv((w) => ({ ...w, logo_alt: e.target.value }))} placeholder="Witness Your Vote official logo" className="mt-1 bg-white" data-testid="admin-wyv-logo-alt-input" />
          </div>
        </div>

        {/* ---- Video + site link (existing controls) ---- */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="wyv-site-url">WitnessYourVote.ca link</Label>
            <Input id="wyv-site-url" value={wyv.site_url} onChange={(e) => setWyv((w) => ({ ...w, site_url: e.target.value }))} placeholder="https://witnessyourvote.ca" className="mt-1" data-testid="admin-wyv-site-input" />
          </div>
          <div>
            <Label htmlFor="wyv-embed-url">Welcome video embed URL (YouTube/Vimeo)</Label>
            <Input id="wyv-embed-url" value={wyv.video_embed_url} onChange={(e) => setWyv((w) => ({ ...w, video_embed_url: e.target.value }))} placeholder="https://www.youtube.com/embed/..." className="mt-1" data-testid="admin-wyv-embed-input" />
          </div>
          <div>
            <Label htmlFor="wyv-video-url">Or direct video file URL (mp4)</Label>
            <Input id="wyv-video-url" value={wyv.video_url} onChange={(e) => setWyv((w) => ({ ...w, video_url: e.target.value }))} placeholder="/api/uploads/wyv-welcome.mp4" className="mt-1" data-testid="admin-wyv-video-input" />
          </div>
        </div>
        <p className="text-xs text-slate-500">If no video is entered, the whole video area stays hidden on the public page.</p>
        <div className="flex justify-end">
          <Button className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" onClick={save} disabled={saving} data-testid="admin-wyv-save-button">
            {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Save WYV Assets
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

/* Phase 109A: series image health. Checks every part's artwork against local
   disk and durable storage, restores parts that have a known durable copy,
   and names the exact file to upload for the rest. */
const PosterImageHealth = ({ onRestored }) => {
  const [health, setHealth] = useState(null);
  const [checking, setChecking] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const check = async () => {
    setChecking(true);
    try {
      const r = await api.get('/admin/paper-ballot-posters/health', { timeout: 120000 });
      setHealth(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not check series images');
    } finally {
      setChecking(false);
    }
  };
  useEffect(() => { check(); }, []);

  const restore = async () => {
    setRestoring(true);
    setConfirmOpen(false);
    try {
      const r = await api.post('/admin/paper-ballot-posters/restore-images', {}, { timeout: 300000 });
      const n = (r.data?.restored || []).length;
      toast.success(n ? `Restored artwork for ${n} part(s)` : 'Nothing to restore from durable copies');
      await check();
      onRestored?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoring(false);
    }
  };

  const missing = (health?.rows || []).filter((r) => r.status !== 'ok');
  const recoverable = missing.filter((r) => r.recoverable_from_durable_copy);
  const uploadNeeded = missing.filter((r) => r.upload_required);

  return (
    <Card className={health && missing.length ? 'border-amber-300 bg-amber-50/40' : 'border-emerald-200 bg-emerald-50/30'} data-testid="poster-image-health">
      <CardContent className="p-4 sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            {health && missing.length ? <AlertTriangle className="h-5 w-5 text-amber-700 mt-0.5" /> : <ShieldCheck className="h-5 w-5 text-emerald-700 mt-0.5" />}
            <div>
              <p className="font-serif font-bold text-[#1a2744]">Series image health</p>
              {!health && <p className="text-sm text-slate-600 mt-0.5">Checking every part's artwork against disk and durable storage...</p>}
              {health && (
                <p className="text-sm text-slate-700 mt-0.5" data-testid="poster-image-health-summary">
                  <strong>{health.ok}</strong> of {health.total} parts have working artwork.
                  {health.missing_file > 0 && <> <strong className="text-amber-800">{health.missing_file}</strong> point at files that no longer exist.</>}
                  {health.no_image > 0 && <> <strong>{health.no_image}</strong> have no artwork set.</>}
                  {recoverable.length > 0 && <> <strong className="text-emerald-800">{recoverable.length}</strong> can be restored from a durable copy now.</>}
                </p>
              )}
              {health && !health.durable_storage_reachable && (
                <p className="text-xs font-bold text-red-700 mt-1" data-testid="poster-image-health-storage-warning">Durable storage could not be reached during this check. Restore will retry when you press it.</p>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={check} disabled={checking} className="h-10 font-bold btn-press" data-testid="poster-image-health-check">
              {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />} Check again
            </Button>
            {recoverable.length > 0 && (
              <Button onClick={() => setConfirmOpen(true)} disabled={restoring} className="h-10 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="poster-image-health-restore">
                {restoring ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-1.5" />} Restore {recoverable.length} from durable copies
              </Button>
            )}
          </div>
        </div>

        {health && missing.length > 0 && (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-xs" data-testid="poster-image-health-table">
              <thead>
                <tr className="text-left text-slate-500">
                  <th className="py-1.5 pr-3">Part</th><th className="py-1.5 pr-3">Status</th><th className="py-1.5 pr-3">Current file</th><th className="py-1.5">What to do</th>
                </tr>
              </thead>
              <tbody>
                {missing.map((r) => (
                  <tr key={r.part} className="border-t border-slate-200 align-top" data-testid={`poster-health-row-${r.part}`}>
                    <td className="py-2 pr-3 font-bold text-[#1a2744]">{r.part}</td>
                    <td className="py-2 pr-3">
                      {r.status === 'missing' ? <Badge className="bg-amber-100 text-amber-900 border border-amber-300">File missing</Badge> : <Badge variant="secondary">No artwork</Badge>}
                    </td>
                    <td className="py-2 pr-3 font-mono text-[10px] text-slate-500 break-all">{r.filename || '-'}</td>
                    <td className="py-2 text-slate-700" data-testid={`poster-health-action-${r.part}`}>
                      {r.recoverable_from_durable_copy
                        ? <span className="inline-flex items-center gap-1 text-emerald-800 font-bold"><CheckCircle2 className="h-3.5 w-3.5" /> Durable copy available. Press Restore.</span>
                        : <span>Upload <strong>{r.expected_upload_name}</strong> (the Part {r.part} poster) via Edit &rarr; Upload artwork. Nothing to recover automatically.</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {uploadNeeded.length > 0 && (
              <p className="text-xs text-slate-600 mt-3" data-testid="poster-image-health-upload-list">
                Files you need to upload again: <strong>{uploadNeeded.map((r) => r.expected_upload_name).join(', ')}</strong>. Every new upload goes to durable storage automatically and survives deployments.
              </p>
            )}
          </div>
        )}
        {health && missing.length === 0 && (
          <p className="text-xs text-emerald-800 mt-2" data-testid="poster-image-health-all-ok">All series artwork is present and mirrored to durable storage.</p>
        )}
      </CardContent>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="poster-image-restore-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Restore poster artwork from durable copies?</AlertDialogTitle>
            <AlertDialogDescription>
              {recoverable.length} part(s) will be pointed at their durable copy: {recoverable.map((r) => `Part ${r.part}`).join(', ')}. Titles, explanations and sources are not changed. Parts without a durable copy are left for you to upload.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="poster-image-restore-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={restore} className="bg-[#1a2744] hover:bg-[#243456]" data-testid="poster-image-restore-confirm-button">Restore</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default function AdminPosters() {
  const [posters, setPosters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // poster object or 'new'
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const fileRef = useRef(null);

  const load = () => api.get('/admin/paper-ballot-posters').then((r) => setPosters(r.data || [])).catch(() => toast.error('Could not load posters')).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openEdit = (p) => {
    if (p === 'new') {
      const nextPart = posters.length ? Math.max(...posters.map((x) => x.part)) + 1 : 1;
      setForm({ ...EMPTY, part: nextPart, title: `Part ${nextPart} of 18` });
    } else {
      setForm({ part: p.part, title: p.title || '', image_url: p.image_url || '', explanation: p.explanation || '', sources_text: p.sources_text || '', article_url: p.article_url || '', article_label: p.article_label || '', share_title: p.share_title || '', share_description: p.share_description || '', share_image_url: p.share_image_url || '', published: p.published !== false });
    }
    setEditing(p);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing === 'new') {
        await api.post('/admin/paper-ballot-posters', { ...form, part: Number(form.part) });
        toast.success('Poster added');
      } else {
        await api.put(`/admin/paper-ballot-posters/${editing.id}`, { ...form, part: Number(form.part) });
        toast.success('Poster saved');
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', 'paper-ballot-posters');
      const r = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm((f) => ({ ...f, image_url: r.data.url }));
      toast.success('Artwork uploaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/paper-ballot-posters/${deleting.id}`);
      toast.success('Poster deleted');
      setDeleting(null);
      load();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-5" data-testid="admin-posters-page">
      <Tabs defaultValue="posters" className="space-y-5">
        <TabsList className="bg-slate-100" data-testid="admin-posters-tabs">
          <TabsTrigger value="posters" className="font-bold" data-testid="admin-tab-posters">Paper Ballot Posters</TabsTrigger>
          <TabsTrigger value="wyv" className="font-bold" data-testid="admin-tab-wyv">Witness Your Vote Assets</TabsTrigger>
        </TabsList>

        <TabsContent value="posters" className="space-y-5">
      <PosterImageHealth onRestored={load} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-black text-[#1a2744]">Paper Ballot Posters</h1>
          <p className="text-sm text-slate-500 mt-1">Manage the 18-part series. Upload or replace artwork, explanations, sources, and related articles. Changes appear instantly on the public Paper Ballots page.</p>
        </div>
        <Button onClick={() => openEdit('new')} className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="admin-posters-add-button">
          <Plus className="h-4 w-4 mr-1.5" /> Add Poster
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading posters...</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="admin-posters-grid">
          {posters.map((p) => (
            <Card key={p.id} className="overflow-hidden" data-testid={`admin-poster-card-${p.part}`}>
              <CardContent className="p-0">
                {p.image_url ? (
                  <img src={fileUrl(p.image_url)} alt={p.title} className="w-full aspect-[3/4] object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling?.classList.remove('hidden'); }} />
                ) : null}
                {p.image_url && (
                  <div className="hidden w-full aspect-[3/4] flex-col items-center justify-center gap-1.5 bg-amber-50 border-b border-amber-200" data-testid={`admin-poster-broken-${p.part}`}>
                    <AlertTriangle className="h-6 w-6 text-amber-700" />
                    <p className="text-[#1a2744] font-black">{p.part}</p>
                    <p className="text-[9px] font-bold uppercase tracking-wider text-amber-800 text-center px-2">Image file missing. See health panel.</p>
                  </div>
                )}
                {!p.image_url && (
                  <div className="w-full aspect-[3/4] flex flex-col items-center justify-center gap-1.5 bg-gradient-to-b from-[#1a2744] to-[#22345c]">
                    <Vote className="h-6 w-6 text-[#C9A84C]" />
                    <p className="text-white font-black">{p.part}</p>
                    <p className="text-[9px] font-bold uppercase tracking-wider text-slate-300">No artwork yet</p>
                  </div>
                )}
                <div className="p-2.5">
                  <div className="flex items-center justify-between gap-1">
                    <p className="text-xs font-bold text-[#1a2744] truncate">{p.title}</p>
                    {p.published === false && <Badge variant="outline" className="text-[9px] bg-slate-100">Hidden</Badge>}
                  </div>
                  <div className="flex gap-1.5 mt-2">
                    <Button size="sm" variant="outline" className="h-8 flex-1 text-xs font-bold btn-press" onClick={() => openEdit(p)} data-testid={`admin-poster-edit-${p.part}`}>
                      <Pencil className="h-3 w-3 mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50 btn-press" onClick={() => setDeleting(p)} data-testid={`admin-poster-delete-${p.part}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                  <Button size="sm" variant="outline" className="h-8 w-full mt-1.5 text-xs font-bold btn-press" data-testid={`admin-poster-fblink-${p.part}`}
                    onClick={async () => {
                      const link = `${window.location.origin}/api/share/poster/${p.part}`;
                      try { await navigator.clipboard.writeText(link); toast.success(`Facebook share link for Part ${p.part} copied`); }
                      catch { toast.error('Could not copy. Link: ' + link); }
                    }}>
                    <Share2 className="h-3 w-3 mr-1" /> Copy Facebook Link
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
        </TabsContent>

        <TabsContent value="wyv" className="space-y-5">
          <div>
            <h1 className="text-2xl font-black text-[#1a2744]">Witness Your Vote Assets</h1>
            <p className="text-sm text-slate-500 mt-1">The official Witness Your Vote logo, welcome video, and site link. Separate from the Paper Ballot posters.</p>
          </div>
          <WyvAssetsCard />
        </TabsContent>
      </Tabs>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="admin-poster-dialog">
          <DialogHeader><DialogTitle>{editing === 'new' ? 'Add poster' : `Edit ${form.title || 'poster'}`}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label htmlFor="poster-part">Part #</Label>
                <Input id="poster-part" type="number" min="1" max="99" value={form.part} onChange={(e) => setForm((f) => ({ ...f, part: e.target.value }))} className="mt-1" data-testid="admin-poster-part-input" />
              </div>
              <div className="col-span-2">
                <Label htmlFor="poster-title">Poster title</Label>
                <Input id="poster-title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="mt-1" data-testid="admin-poster-title-input" />
              </div>
            </div>
            <div>
              <Label>Poster artwork</Label>
              <div className="mt-1 flex items-center gap-3">
                {form.image_url ? (
                  <img src={fileUrl(form.image_url)} alt="Poster preview" className="h-24 w-auto rounded-lg border border-slate-200" data-testid="admin-poster-preview" />
                ) : (
                  <div className="h-24 w-[72px] rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center"><Vote className="h-5 w-5 text-slate-400" /></div>
                )}
                <div className="space-y-2">
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} data-testid="admin-poster-file-input" />
                  <Button variant="outline" className="h-10 font-bold btn-press" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="admin-poster-upload-button">
                    {uploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />} {form.image_url ? 'Replace artwork' : 'Upload artwork'}
                  </Button>
                  {form.image_url && (
                    <Button variant="outline" className="h-10 text-xs text-red-600 border-red-200 hover:bg-red-50 btn-press block" onClick={() => setForm((f) => ({ ...f, image_url: '' }))} data-testid="admin-poster-remove-image">Remove image</Button>
                  )}
                </div>
              </div>
            </div>
            <div>
              <Label htmlFor="poster-explanation">Short explanation</Label>
              <Textarea id="poster-explanation" rows={3} value={form.explanation} onChange={(e) => setForm((f) => ({ ...f, explanation: e.target.value }))} className="mt-1" data-testid="admin-poster-explanation-input" />
            </div>
            <div>
              <Label htmlFor="poster-sources">Sources (one per line: Label | URL)</Label>
              <Textarea id="poster-sources" rows={3} value={form.sources_text} onChange={(e) => setForm((f) => ({ ...f, sources_text: e.target.value }))} placeholder={'Municipal Elections Act | https://www.ontario.ca/laws/statute/96m32'} className="mt-1 font-mono text-xs" data-testid="admin-poster-sources-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="poster-article-url">Related article URL</Label>
                <Input id="poster-article-url" value={form.article_url} onChange={(e) => setForm((f) => ({ ...f, article_url: e.target.value }))} placeholder="/newsroom/my-article or https://..." className="mt-1" data-testid="admin-poster-article-url-input" />
              </div>
              <div>
                <Label htmlFor="poster-article-label">Article label</Label>
                <Input id="poster-article-label" value={form.article_label} onChange={(e) => setForm((f) => ({ ...f, article_label: e.target.value }))} placeholder="Read the article" className="mt-1" data-testid="admin-poster-article-label-input" />
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 p-3 space-y-3" data-testid="admin-poster-share-fields">
              <p className="text-xs font-black uppercase tracking-wider text-slate-500">Sharing and SEO (each poster has its own URL: /paper-ballots/{String(form.part || '')})</p>
              <div>
                <Label htmlFor="poster-share-title">Share / page title (optional)</Label>
                <Input id="poster-share-title" value={form.share_title} onChange={(e) => setForm((f) => ({ ...f, share_title: e.target.value }))} placeholder="Defaults to the poster title" className="mt-1" data-testid="admin-poster-share-title-input" />
              </div>
              <div>
                <Label htmlFor="poster-share-desc">Meta / share description (optional)</Label>
                <Textarea id="poster-share-desc" rows={2} value={form.share_description} onChange={(e) => setForm((f) => ({ ...f, share_description: e.target.value }))} placeholder="Defaults to the explanation" className="mt-1" data-testid="admin-poster-share-desc-input" />
              </div>
              <div>
                <Label htmlFor="poster-share-image">Share image URL (optional)</Label>
                <Input id="poster-share-image" value={form.share_image_url} onChange={(e) => setForm((f) => ({ ...f, share_image_url: e.target.value }))} placeholder="Defaults to the poster artwork" className="mt-1" data-testid="admin-poster-share-image-input" />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
              <div>
                <p className="text-sm font-bold text-[#1a2744]">Published</p>
                <p className="text-xs text-slate-500">Hidden posters do not appear on the public page.</p>
              </div>
              <Switch checked={form.published} onCheckedChange={(v) => setForm((f) => ({ ...f, published: v }))} data-testid="admin-poster-published-switch" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" className="h-11 font-bold btn-press" onClick={() => setEditing(null)} data-testid="admin-poster-cancel-button">Cancel</Button>
              <Button className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" onClick={save} disabled={saving} data-testid="admin-poster-save-button">
                {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleting} onOpenChange={(v) => { if (!v) setDeleting(null); }}>
        <AlertDialogContent data-testid="admin-poster-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this poster?</AlertDialogTitle>
            <AlertDialogDescription>{deleting ? `"${deleting.title}" will be removed from the series. This cannot be undone.` : ''}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-poster-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="admin-poster-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
